import initProductVariants from './product-variants';
import initQuantityInputs from './quantityInputs';
import initAtcForm from './atcForm';
import { initSwiper } from './swiper';
import trapFocus from '../utils/trapFocus';
import getLocalePrefix from '../lib/getLocalePrefix';
import { SELECTORS } from '../utils/constants';

let drawerInitialized = false;
const OPEN_CLASS = 'site-drawer--open';
const HTML_OPEN_CLASS = 'site-drawer-open';
const ARIA_HIDDEN = 'aria-hidden';
const POINTER_EVENTS_NONE_CLASS = 'pointer-events-none';
const LOADING_OPACITY_CLASS = 'opacity-60';
const QUICK_ADD_SECTION_ID = 'quick-add-product';
// Slightly over the panel's 320ms close transition (see snippets/drawer.liquid).
const DRAWER_CLEANUP_FALLBACK_MS = 400;

const initDrawer = () => {
  if (drawerInitialized) return;

  const drawer = document.querySelector('[data-site-drawer]');
  if (!drawer) return;

  const panel = drawer.querySelector('[data-site-drawer-panel]');
  const titleEl = drawer.querySelector('[data-site-drawer-title]');
  const contentEl = drawer.querySelector('[data-site-drawer-content]');
  const closeBtn = drawer.querySelector('[data-site-drawer-close]');
  if (!panel || !titleEl || !contentEl) return;

  let activeTrigger = null;
  let untrapFocus = null;
  let lockedScrollY = 0;

  // Focus returned programmatically after a pointer-initiated close would
  // otherwise paint the browser's focus ring on the trigger (Android Chrome
  // treats script focus as focus-visible). Track the last input modality and
  // suppress the ring visually for pointer closes only — focus still moves,
  // so keyboard and screen-reader users are unaffected.
  let isPointerInteraction = false;
  document.addEventListener('pointerdown', () => { isPointerInteraction = true; }, true);
  document.addEventListener('keydown', () => { isPointerInteraction = false; }, true);

  const focusActiveTrigger = () => {
    const triggerEl = activeTrigger;
    if (!triggerEl) return;
    if (isPointerInteraction) {
      triggerEl.dataset.suppressFocusRing = 'true';
      triggerEl.addEventListener('blur', () => {
        delete triggerEl.dataset.suppressFocusRing;
      }, { once: true });
    }
    triggerEl.focus({ preventScroll: true });
  };

  const setLoading = (trigger, loading) => {
    const spinner = trigger?.querySelector(SELECTORS.LOADING_SPINNER);
    trigger?.classList.toggle(POINTER_EVENTS_NONE_CLASS, loading);
    trigger?.classList.toggle(LOADING_OPACITY_CLASS, loading);
    trigger?.setAttribute('aria-busy', loading ? 'true' : 'false');
    spinner?.classList.toggle('hidden', !loading);
  };

  const openDrawer = (title, content, trigger) => {
    activeTrigger = trigger;
    titleEl.textContent = title;
    contentEl.innerHTML = content;
    panel.scrollTop = 0;
    // Safari (iOS and macOS) collapses the page scroll to 0 when the root
    // becomes overflow:hidden (Chrome/Firefox freeze it in place) — remember
    // the position so closeDrawer can restore it.
    lockedScrollY = window.scrollY;
    document.documentElement.classList.add(HTML_OPEN_CLASS);
    drawer.hidden = false;
    drawer.removeAttribute(ARIA_HIDDEN);
    requestAnimationFrame(() => {
      drawer.classList.add(OPEN_CLASS);
      untrapFocus?.();
      untrapFocus = trapFocus(drawer);
      closeBtn?.focus();
    });
  };

  const cleanupClosedDrawer = () => {
    if (drawer.classList.contains(OPEN_CLASS)) return;

    drawer.hidden = true;
    contentEl.innerHTML = '';
    titleEl.textContent = '';
  };

  const closeDrawer = () => {
    if (!drawer.classList.contains(OPEN_CLASS)) return;
    focusActiveTrigger();
    activeTrigger = null;
    untrapFocus?.();
    untrapFocus = null;
    drawer.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove(HTML_OPEN_CLASS);
    // Restore the pre-open scroll position — Safari zeroes it while the root
    // is overflow:hidden; on other browsers this is a same-position no-op.
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
    drawer.setAttribute(ARIA_HIDDEN, 'true');
    // Fallback for when no transition runs (e.g. prefers-reduced-motion):
    // cleanupClosedDrawer self-guards on the open class, so a late call after
    // a quick reopen is a no-op and a double call is harmless.
    window.setTimeout(cleanupClosedDrawer, DRAWER_CLEANUP_FALLBACK_MS);
  };

  const openTemplateDrawer = (templateKey, trigger) => {
    const template = document.querySelector(`[data-drawer-template="${templateKey}"]`);
    if (!template) return;

    openDrawer(template.dataset.drawerTitle || '', template.innerHTML, trigger);
  };

  const getQuickAddProduct = async (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', QUICK_ADD_SECTION_ID);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);

    const htmlText = await response.text();
    const doc = new DOMParser().parseFromString(htmlText, 'text/html');
    const quickAddProduct = doc.querySelector('[data-quick-add-drawer-product]');
    if (!quickAddProduct) {
      throw new Error(`Quick-add product element missing from section response for ${url.href}`);
    }

    return quickAddProduct;
  };

  const initQuickAddGallery = () => {
    const gallery = drawer.querySelector('[data-quick-add-gallery]');
    if (!gallery || gallery.dataset.dragScrollInitialized === 'true') return;

    gallery.dataset.dragScrollInitialized = 'true';

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    gallery.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      isDragging = true;
      startX = event.clientX;
      startScrollLeft = gallery.scrollLeft;
      gallery.setPointerCapture(event.pointerId);
    });

    gallery.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      gallery.scrollLeft = startScrollLeft - (event.clientX - startX);
    });

    const stopDragging = (event) => {
      if (!isDragging) return;

      isDragging = false;

      if (gallery.hasPointerCapture(event.pointerId)) {
        gallery.releasePointerCapture(event.pointerId);
      }
    };

    gallery.addEventListener('pointerup', stopDragging);
    gallery.addEventListener('pointercancel', stopDragging);
  };

  const initQuickAddProduct = () => {
    initProductVariants(drawer);
    initQuantityInputs(drawer);
    initAtcForm(true, false, closeDrawer, drawer);
    initQuickAddGallery();
    const swiperEl = drawer.querySelector('[data-swiper]');
    if (swiperEl) initSwiper(swiperEl);
    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      window.yotpoWidgetsContainer.initWidgets();
    }
  };

  const openQuickAddDrawer = async (trigger) => {
    setLoading(trigger, true);
    const localePrefix = getLocalePrefix();

    try {
      const productUrl = `${window.location.origin}${localePrefix}/products/${trigger.dataset.productCardAtc}`;
      const quickAddProduct = await getQuickAddProduct(productUrl);
      if (!quickAddProduct) return;
      openDrawer(window.theme.strings.quickAdd, quickAddProduct.outerHTML, trigger);
      initQuickAddProduct();
    } catch (error) {
      console.error('Error opening quick add drawer:', error);
    } finally {
      setLoading(trigger, false);
    }
  };

  const switchQuickAddProduct = async (productUrl) => {
    const currentProduct = contentEl.querySelector('[data-quick-add-drawer-product]');
    currentProduct?.classList.add(POINTER_EVENTS_NONE_CLASS, LOADING_OPACITY_CLASS);
    panel.setAttribute('aria-busy', 'true');

    try {
      const quickAddProduct = await getQuickAddProduct(productUrl);
      if (!quickAddProduct) return;

      contentEl.innerHTML = quickAddProduct.outerHTML;
      initQuickAddProduct();
      untrapFocus?.();
      untrapFocus = trapFocus(drawer);
      contentEl.querySelector('[data-quick-add-product-title]')?.focus();
    } catch (error) {
      console.error('Error switching quick add product:', error);
      currentProduct?.classList.remove(POINTER_EVENTS_NONE_CLASS, LOADING_OPACITY_CLASS);
    } finally {
      panel.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('click', (event) => {
    const templateTrigger = event.target.closest('[data-drawer-open]');
    const quickAddTrigger = event.target.closest('[data-quick-add-trigger]');

    if (templateTrigger) {
      openTemplateDrawer(templateTrigger.dataset.drawerOpen, templateTrigger);
      return;
    }

    if (quickAddTrigger) {
      openQuickAddDrawer(quickAddTrigger);
    }
  });

  drawer.addEventListener('mousedown', (event) => {
    if (event.target === drawer) closeDrawer();
  });

  drawer.addEventListener('click', (event) => {
    const linkedProduct = event.target.closest('[data-quick-add-linked-product]');
    if (!linkedProduct) return;

    event.preventDefault();
    switchQuickAddProduct(linkedProduct.href);
  });

  drawer.addEventListener('change', (event) => {
    const linkedSizeSelect = event.target.closest('[data-linked-size-products]');
    if (!linkedSizeSelect) return;

    if (linkedSizeSelect.value
      && linkedSizeSelect.value !== linkedSizeSelect.dataset.currentProductUrl) {
      switchQuickAddProduct(linkedSizeSelect.value);
    }
  });

  closeBtn?.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer.classList.contains(OPEN_CLASS)) closeDrawer();
  });

  // Tailwind 4's translate-x-* utilities animate the standalone `translate`
  // property, not `transform` — the close transition ends with
  // propertyName 'translate'.
  panel.addEventListener('transitionend', (event) => {
    if (event.target !== panel) return;
    if (event.propertyName !== 'transform' && event.propertyName !== 'translate') return;
    cleanupClosedDrawer();
  });

  drawerInitialized = true;
};

export default initDrawer;
